import classnames from 'classnames'
import { LoanFilterTypes } from 'embed/constants'
import { LoanOffer } from 'embed/constants/models'
import { css } from 'emotion'
import * as React from 'react'

import ThemeConsumer from 'embed/components/ThemeConsumer'
import { getContrastingColor, pluralize } from 'embed/utils'
import { Slider } from '../../Slider'
import { ButtonToggle } from '../../Toggles/ButtonToggle'
import { ButtonToggleList } from '../../Toggles/ButtonToggleList/container'

import { applyFilters, FilterState, getLoanAndMonthlyPaymentRanges, RangeFilter } from './utils'
export { applyFilters } from './utils'

export enum DisplayOfferType {
  Loan = 'loan',
  Special = 'special',
}

const styles = require('./styles.scss')
const DoubleChevron = require('./double-chevron.svg')

export interface FilterDrawerProps {
  loanOffers: LoanOffer[]
  setFilterState: (filterState: FilterState) => void
  drawerText?: string
}

export const FilterDrawer: React.FunctionComponent<FilterDrawerProps> = (props) => {
  // PROPS
  const { loanOffers, setFilterState, drawerText } = props

  // INTERNAL STATE
  const [isExpanded, setIsExpanded] = React.useState(false)
  const [internalFilterState, setInternalFilterState] = React.useState<FilterState>({ selectedLenders: [] })

  // SETTERS
  const setLoanAmountRange = (range: RangeFilter) =>
    setInternalFilterState({ ...internalFilterState, loanAmountRange: range })
  const setMonthlyPaymentRange = (range: RangeFilter) =>
    setInternalFilterState({ ...internalFilterState, monthlyPaymentRange: range })

  // DERIVED VALUES
  const [offerLoanAmountRange, offerMonthlyPaymentRange] = getLoanAndMonthlyPaymentRanges(loanOffers)
  const { selectedLenders, loanAmountRange, monthlyPaymentRange } = internalFilterState
  const previewOfferCount = applyFilters(internalFilterState, loanOffers).length

  // FIX ACTIVE LENDERS
  // When selected lenders in internal state are missing from offers, remove them
  const offerLenders = new Set(loanOffers.map((offer) => offer.originator?.companyUuid))
  const reconciledLenders = selectedLenders.filter((lender) => offerLenders.has(lender))
  if (reconciledLenders.length !== selectedLenders.length) {
    setInternalFilterState({ ...internalFilterState, selectedLenders: reconciledLenders })
  }

  // CALLBACKS
  const toggleLender = (toggle: ButtonToggle) => {
    if (new Set(selectedLenders).has(toggle.value)) {
      // Remove a lender
      setInternalFilterState({
        ...internalFilterState,
        selectedLenders: selectedLenders.filter((lender) => lender !== toggle.value),
      })
    } else {
      // Add a lender
      setInternalFilterState({ ...internalFilterState, selectedLenders: [...selectedLenders, toggle.value] })
    }
  }

  const toggleFilterContainer = () => setIsExpanded(!isExpanded)

  const resetFilters = () => {
    setIsExpanded(false)
    setInternalFilterState({ selectedLenders: [] })
    pushFilterState({ selectedLenders: [] })
  }

  const onClickResetFilters = (e) => resetFilters()

  const pushFilterState = (filterState?: FilterState) => {
    setIsExpanded(false)
    setFilterState(filterState ? { ...filterState } : { ...internalFilterState })
  }

  const onClickPushFilterState = (e) => pushFilterState()

  const shouldRenderDrawer = () =>
    loanOffers.length > 1 &&
    offerLenders.size > 1 &&
    offerLoanAmountRange.min !== offerLoanAmountRange.max &&
    offerMonthlyPaymentRange.min !== offerMonthlyPaymentRange.max

  const distinctOriginators = Array.from(
    loanOffers
      .reduce((map, current) => map.set(current.originator?.companyUuid, current.originator), new Map())
      .values(),
  )

  // SUBCOMPONENTS
  const lenderBadges: ButtonToggle[] = distinctOriginators.map((originator) => ({
    active: selectedLenders.indexOf(originator.companyUuid) > -1,
    value: originator.companyUuid,
    label: originator.name,
    filterCategory: LoanFilterTypes.LENDERS,
  }))

  const applyButtonText = `See ${previewOfferCount} ${pluralize('Offer', previewOfferCount)}`

  return (
    <ThemeConsumer>
      {({
        backdropStyles,
        colorBorder,
        borderRadiusLarge,
        boxStyles,
        fontFamilyPrimary,
        colorPrimary,
        colorTextSecondary,
      }) => (
        <React.Fragment>
          <div
            className={classnames(
              styles.filterContainer,
              css`
                border-radius: ${borderRadiusLarge};
              `,
            )}
          >
            <div
              className={classnames(
                styles.header,
                css`
                  background-color: ${backdropStyles.backgroundColor};
                  border-color: ${colorBorder};
                  border-radius: ${borderRadiusLarge};
                `,
              )}
              style={boxStyles}
            >
              {drawerText && (
                <h2
                  className={css`
                    font-weight: normal;
                    text-align: center;
                    margin: 0;
                  `}
                >
                  {drawerText}
                </h2>
              )}
            </div>
            {shouldRenderDrawer() && (
              <div>
                {isExpanded && (
                  <div className={classnames(styles.resultsFiltersContainer, styles.column)}>
                    {offerLenders.size > 0 && (
                      <ButtonToggleList title="Lenders" toggleBadge={toggleLender} badges={lenderBadges} />
                    )}
                    <Slider
                      title="Monthly Payment"
                      initial={offerMonthlyPaymentRange}
                      current={monthlyPaymentRange ?? offerMonthlyPaymentRange}
                      onChange={setMonthlyPaymentRange}
                      thumbs={1}
                    />
                    <Slider
                      title="Loan Amount"
                      initial={offerLoanAmountRange}
                      current={loanAmountRange ?? offerLoanAmountRange}
                      onChange={setLoanAmountRange}
                      thumbs={2}
                    />
                    <div className={styles.buttonRow}>
                      <button
                        onClick={onClickResetFilters}
                        className={css`
                          outline: none;
                          background-color: #ffffff;
                          color: ${colorPrimary};
                        `}
                      >
                        Clear Filters
                      </button>
                      <button
                        disabled={previewOfferCount === 0}
                        className={css`
                          outline: none;
                          color: #ffffff;
                          background-color: ${colorPrimary};
                        `}
                        onClick={onClickPushFilterState}
                      >
                        {previewOfferCount > 0 && <span>{applyButtonText}</span>}
                        {previewOfferCount === 0 && <span>No Offers Available</span>}
                      </button>
                    </div>
                  </div>
                )}
                <button
                  className={classnames(
                    styles.filterToggle,
                    css`
                      color: ${colorTextSecondary};
                      font-family: ${fontFamilyPrimary};
                      svg path {
                        fill: ${colorTextSecondary};
                      }
                      &:hover {
                        color: ${colorTextSecondary};
                        svg path {
                          fill: ${colorTextSecondary};
                        }
                      }
                    `,
                  )}
                  data-test-id="filter_toggle"
                  onClick={toggleFilterContainer.bind(null, true)}
                >
                  {(selectedLenders.length ?? 0) > 0 && (
                    <span
                      className={classnames(
                        styles.numberOfActiveFiltersBadge,
                        css`
                          background-color: ${colorPrimary};
                          color: ${getContrastingColor(colorPrimary)};
                        `,
                      )}
                    >
                      {selectedLenders.length}
                    </span>
                  )}
                  <span>Filter</span>
                  <DoubleChevron className={classnames(styles.chevron, { [styles.chevronFlipped]: isExpanded })} />
                </button>
              </div>
            )}
          </div>
        </React.Fragment>
      )}
    </ThemeConsumer>
  )
}
